import { useEffect, useRef, useState } from "react";

interface Props {
  onRecorded: (file: File) => void;
  onClose: () => void;
  maxSeconds?: number;
}

type Kind = "audio" | "video";

export function VoiceNoteRecorder({ onRecorded, onClose, maxSeconds = 60 }: Props) {
  const [kind, setKind] = useState<Kind>("audio");
  const [status, setStatus] = useState<"idle" | "recording" | "preview" | "error">("idle");
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement | null>(null);

  const cleanup = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
  };

  useEffect(() => () => cleanup(), []);

  useEffect(() => {
    if (status === "recording" && kind === "video" && videoPreviewRef.current && streamRef.current) {
      videoPreviewRef.current.srcObject = streamRef.current;
    }
  }, [status, kind]);

  const start = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError("Your browser cannot record here. Use the file picker instead.");
      setStatus("error");
      return;
    }
    try {
      const constraints = kind === "video" ? { video: true, audio: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const mime =
        kind === "video"
          ? MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus")
            ? "video/webm;codecs=vp8,opus"
            : MediaRecorder.isTypeSupported("video/mp4")
              ? "video/mp4"
              : ""
          : MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
            ? "audio/webm;codecs=opus"
            : MediaRecorder.isTypeSupported("audio/mp4")
              ? "audio/mp4"
              : "";
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || (kind === "video" ? "video/webm" : "audio/webm");
        setBlob(new Blob(chunksRef.current, { type }));
        setStatus("preview");
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setStatus("recording");
      setSeconds(0);
      const startedAt = Date.now();
      timerRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        setSeconds(elapsed);
        if (elapsed >= maxSeconds) stop();
      }, 250);
    } catch (e) {
      setError("Permission was denied or is unavailable.");
      setStatus("error");
    }
  };

  const stop = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) window.clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  const submit = () => {
    if (!blob) return;
    const ext =
      kind === "video"
        ? blob.type.includes("mp4")
          ? "mp4"
          : "webm"
        : blob.type.includes("mp4")
          ? "m4a"
          : "webm";
    const file = new File([blob], `${kind === "video" ? "video-toast" : "voice-note"}-${Date.now()}.${ext}`, {
      type: blob.type,
    });
    onRecorded(file);
    cleanup();
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "1rem",
      }}
      onClick={() => {
        if (status !== "recording") {
          cleanup();
          onClose();
        }
      }}
    >
      <div
        style={{ background: "#fff", borderRadius: "12px", padding: "1.5rem", maxWidth: "480px", width: "100%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0 }}>Record a {kind === "video" ? "video toast" : "voice note"}</h2>
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
          <button type="button" onClick={() => setKind("audio")} disabled={status === "recording"}>
            Audio
          </button>
          <button type="button" onClick={() => setKind("video")} disabled={status === "recording"}>
            Video
          </button>
        </div>
        {status === "idle" && (
          <button type="button" onClick={start}>
            Start recording
          </button>
        )}
        {status === "recording" && (
          <>
            <p>Recording… {seconds}s / {maxSeconds}s</p>
            {kind === "video" && (
              <video
                ref={videoPreviewRef}
                autoPlay
                muted
                playsInline
                style={{ width: "100%", borderRadius: "8px", marginBottom: "0.5rem" }}
              />
            )}
            <button type="button" onClick={stop}>
              Stop
            </button>
          </>
        )}
        {status === "preview" && blob && (
          <>
            {kind === "video" ? (
              <video controls src={URL.createObjectURL(blob)} style={{ width: "100%", borderRadius: "8px" }} />
            ) : (
              <audio controls src={URL.createObjectURL(blob)} style={{ width: "100%" }} />
            )}
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
              <button type="button" onClick={() => { setBlob(null); setStatus("idle"); setSeconds(0); }}>
                Re-record
              </button>
              <button type="button" onClick={submit}>
                Use this {kind === "video" ? "toast" : "note"}
              </button>
            </div>
          </>
        )}
        {status === "error" && <p style={{ color: "var(--danger, #c00)" }}>{error}</p>}
        <div style={{ marginTop: "0.75rem" }}>
          <button type="button" onClick={() => { cleanup(); onClose(); }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
